import { observable, flow, action } from 'mobx';
import { RootStore } from '../createStore';
import { apiState, apiResponse, ApiError } from '../../Api';
import { getAgendamentoBanca, getLocais, confirmarAgendamentoBanca, cancelaAgendamentoBanca, alterarLinkDoEvento } from './Service';
import { ValidationError } from '../IValidator';
import { MonografiaModel } from '../Monografia';
import { format } from 'date-fns';

export class AgendamentoStore {

  @observable public monografia: MonografiaModel = new MonografiaModel();
  @observable public dataSugerida: Date = new Date();
  @observable public locais: Array<{ id: number, nome: string }> = observable([])

  //Dados para o agendamento
  @observable public dia: string = '';
  @observable public hora: string = '';
  @observable public localId: number | string = '';
  @observable public linkEvento: string = '';
  @observable public justificativaExclusao: string = '';

  @observable agendamentoState: apiState = apiState.loading;

  @observable public agendamentoRealizado: boolean = false;
  @observable public agendamentoValidationError: ValidationError<{ dataHora: string, localId: string }>;
  @observable public justificativaValidationError: ValidationError<{ justificativa: string }>

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @action
  updateAttribute(name: string, value: any) {
    this[name] = value;
  }

  @action
  setValidationError = (validationError: ValidationError<{ dataHora: string, localId: string }>) => {
    this.agendamentoValidationError = validationError;
  }

  @action
  setJustificativaValidationError = (validationError: ValidationError<{ justificativa: string }>) => {
    this.justificativaValidationError = validationError;
  }

  @action
  resetAgendamento = () => this.agendamentoRealizado = false;

  getAgendamentoAndLocais = async (monografiaId: number) => {
    await this.getAgendamentoBanca(monografiaId);
    if (this.agendamentoState === apiState.done)
      this.getLocais();
  }

  getAgendamentoBanca = flow(function* (this: AgendamentoStore, monografiaId: number) {
    this.agendamentoState = apiState.loading;

    const result: apiResponse<MonografiaModel | undefined> = yield getAgendamentoBanca(monografiaId);
    this.agendamentoState = result.status;

    if (result.status === apiState.done) {
      this.monografia = result.data!;
      this.dataSugerida = result.data!.dataDeApresentacaoSugerida!;

      this.dia = format(this.dataSugerida, 'YYYY-MM-DD');
      this.hora = format(this.dataSugerida, 'HH:mm');

      if (result.data!.agendamento)
        this.agendamentoRealizado = true;
    }
  })

  getLocais = flow(function* (this: AgendamentoStore) {
    this.agendamentoState = apiState.loading;

    const result: apiResponse<{ id: number, nome: string }[]> = yield getLocais();
    this.agendamentoState = result.status;

    if (result.status === apiState.done) {
      this.locais = result.data || [];
      const selecionado = this.locais.some(l => String(l.id) === String(this.localId));
      if (!selecionado)
        this.localId = '';
    }
  })

  confirmarAgendamentoBanca = flow(function* (this: AgendamentoStore, monografiaId: number) {
    this.agendamentoRealizado = false;
    this.agendamentoState = apiState.loading;

    const local = this.locais.find(l => String(l.id) === String(this.localId));
    if (!local) {
      this.agendamentoState = apiState.done;
      this.setValidationError({ localId: ['Sala é obrigatória'] });
      return;
    }

    const agendamento: apiResponse<ApiError[]> = yield confirmarAgendamentoBanca(
      monografiaId, local!.id, local!.nome, this.dia, this.hora, this.linkEvento);

    this.agendamentoState = agendamento.status;
    this.agendamentoRealizado = this.agendamentoState === apiState.done;

    if (agendamento.status !== apiState.done && agendamento.data)
      this.agendamentoValidationError!.global = agendamento.data.flatMap(err => err.errorMessages);
  })

  alterarLinkDoEvento = flow(function* (this: AgendamentoStore, monografiaId: number,linkEvento: string,) {
    this.agendamentoState = apiState.loading;

    const agendamento: apiResponse<ApiError[]> = yield alterarLinkDoEvento(
      monografiaId, linkEvento);

    this.agendamentoState = agendamento.status;

  })

  cancelaAgendamentoBanca = flow(function* (this: AgendamentoStore, monografiaId: number) {
    this.agendamentoRealizado = true;
    this.agendamentoState = apiState.loading;

    const agendamento: apiResponse<ApiError[]> = yield cancelaAgendamentoBanca(monografiaId, this.justificativaExclusao);
    this.agendamentoState = agendamento.status;

    if (agendamento.status !== apiState.done) {
      this.setValidationError({});
      this.agendamentoValidationError!.global = agendamento.data.flatMap(err => err.errorMessages);
    }
    else {
      this.monografia.agendamento = undefined;
      this.dia = '';
      this.hora = '';
      this.linkEvento = '';
      this.resetAgendamento();
    }
  });
}